import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    domainEventBus,
    WindowLevelDeltaEvent,
    WindowLevelSetEvent,
} from '~/domain/events';
import {
    requireWindowingPreset,
    WindowingPresetName,
    WindowLevel,
} from '~/domain/patient/windowLevel';
import type { Image3D } from '~/domain/patient/image3d';

/**
 * The window/level in force on the series at the moment the toggle was
 * switched on — what switching it off (or an explicit `release`) restores,
 * and what a foreign windowing change or a series switch discards
 * unpublished. An unmount does NEITHER: it leaves the capture behind
 * untouched, deliberately (see the hook's own doc comment below).
 */
interface CapturedWindowLevel {
    seriesUid: string;
    originalWl: WindowLevel;
}

export interface ReviewWindowLevel {
    /** Whether this hook currently owns the display's window/level. */
    isActive: boolean;
    /** Flip the review window on (capture + apply the named preset) or
     *  off (restore the capture), mirroring a press of the review
     *  button. */
    toggle: () => void;
    /** Applies the review window if it is not already applied. Idempotent,
     *  unlike {@link toggle}, so a caller may apply it on open. */
    activate: () => void;
    /** Give the display back and forget the capture, without waiting for
     *  a second press — for releasing on some event other than the user
     *  pressing the toggle again. A no-op while inactive. */
    release: () => void;
}

/**
 * Review window/level TOGGLE, shared by every panel that needs a
 * "show me this" display shortcut (threshold segmentation's Bone / High
 * density tabs; the couch-model tool's Couch preset). Both directions
 * flip the display through the SAME event path as the viewport preset
 * menu, so the whole feature stays exactly as transient and untracked as
 * a manual window/level change: nothing is persisted, and every ordinary
 * windowing control still overrides it at any moment.
 *
 * `image` is the series the caller acts on: the caller resolves its
 * target (on the grid, the edit target's image rather than the globally
 * selected one) and the hook windows exactly that series, so the toggle
 * and the panel it sits in can never window two different images.
 * `undefined` means the caller has no image yet; `toggle` is then a no-op.
 *
 * `presetName` is resolved BY NAME through {@link requireWindowingPreset}
 * on every activation — never hardcoded ww/wc here or in a caller — so a
 * caller stays honest with whatever the viewport preset menu offers under
 * that name. `undefined` means the caller has no review window at all (a
 * tab that renders no toggle button); `toggle` is then a no-op rather
 * than a throw, so the absence is stated in the type instead of by a
 * never-resolved placeholder string.
 *
 * Does not restore on unmount unless the caller asks for it — see
 * {@link ReviewWindowLevelOptions.restoreOnUnmount}. Without it, closing a
 * panel (or switching tabs without going through `release`) leaves the
 * review window applied until the user explicitly switches it off, picks
 * another preset, or windows the series by hand: the same as any other
 * transient windowing change, and, for a named house preset, visible as
 * such in the viewport's own preset menu.
 */
export interface ReviewWindowLevelOptions {
    /**
     * Give the display back when the caller unmounts.
     *
     * Off by default, and deliberately: an unmount effect fires on every
     * teardown path, including ones the user never triggered. A caller
     * that only ever activates on an explicit press wants the review
     * window to outlive its panel, the same as any hand-picked preset.
     * A caller that applies the window ON OPEN wants the opposite — it
     * took the display without being asked, so it gives it back the same
     * way.
     *
     * The restore is skipped when the captured series is no longer the
     * selected one, which is the teardown this hook otherwise refuses to
     * publish on: a primary-image switch tears the caller down along with
     * everything else, and that series' display is not this hook's to
     * touch any more.
     */
    restoreOnUnmount?: boolean;
}

export function useReviewWindowLevel(
    presetName: WindowingPresetName | undefined,
    image: Image3D | undefined,
    { restoreOnUnmount = false }: ReviewWindowLevelOptions = {}
): ReviewWindowLevel {
    // The display window is a property of the SERIES, and the series is
    // the given image's.
    const seriesUid = image?.seriesUid;

    const [captured, setCaptured] = useState<CapturedWindowLevel | null>(null);
    /**
     * The capture, mirrored outside React state. Two things need it: the
     * publish on `release` must happen in `release`'s own body rather
     * than inside a `setCaptured` updater (updaters must be pure, and
     * StrictMode double-invokes them in development — publishing there
     * raised the domain event twice per release), and the series-change
     * effect below must read the capture without depending on it.
     * Written ONLY through {@link applyCaptured}, never during render, so
     * the ref and the state can never disagree.
     */
    const capturedRef = useRef<CapturedWindowLevel | null>(null);
    const applyCaptured = useCallback((next: CapturedWindowLevel | null) => {
        capturedRef.current = next;
        setCaptured(next);
    }, []);
    const isActive = captured !== null;

    /**
     * Raised while this hook is inside its own `publishSync`, so the
     * foreign-change subscription below can tell the hook's own event
     * apart from one raised by any other windowing control. Payload
     * comparison would not do it: the user is free to dial the very same
     * window by hand, and that must still count as taking the display
     * back. `publishSync` invokes its subscribers inline, so the flag is
     * guaranteed up when our own handler runs and down for everyone
     * else's event.
     */
    const isSelfPublishingRef = useRef(false);
    const publish = useCallback(
        (windowLevel: WindowLevel, targetSeriesUid: string) => {
            isSelfPublishingRef.current = true;
            try {
                domainEventBus.publishSync(
                    new WindowLevelSetEvent(
                        windowLevel,
                        targetSeriesUid,
                        'user'
                    )
                );
            } finally {
                isSelfPublishingRef.current = false;
            }
        },
        []
    );

    /** Give the display back and forget the capture. A no-op while
     *  inactive, so callers that release unconditionally (a tab switch
     *  that may or may not currently own the window) need no guard of
     *  their own. */
    const release = useCallback(() => {
        const current = capturedRef.current;
        if (!current) {
            return;
        }
        // Cleared BEFORE publishing, so a second `release` in the same
        // tick (and `publishSync`'s inline subscribers, which run before
        // this returns) both see an already-released hook.
        applyCaptured(null);
        publish(current.originalWl, current.seriesUid);
    }, [applyCaptured, publish]);

    /** Take the display and remember what was there. A no-op while
     *  already active, so a caller that activates on open needs no guard
     *  of its own — including against StrictMode's double-invoked mount
     *  effect, which would otherwise activate and immediately release. */
    const activate = useCallback(() => {
        // Read through the ref, not the state: including `captured` in the
        // dependencies below would give this a new identity on every
        // capture and release, and a caller that activates in an effect
        // would then re-activate the instant the user switched the review
        // window off.
        if (capturedRef.current) {
            return;
        }
        if (!seriesUid || !image || presetName === undefined) {
            return;
        }
        const windowingPreset = requireWindowingPreset(presetName);
        // The window/level currently in force on the series: the engine
        // writes it onto the Image3D from the interaction path, so this
        // is the very value the viewports display. It is undefined only
        // before the engine has windowed the image at all, and is then
        // resolved through the image's own default chain, which is what
        // the display would fall back to as well.
        const originalWl = image.windowLevel ?? image.getDefaultWindowLevel();
        publish(
            new WindowLevel(windowingPreset.ww, windowingPreset.wc),
            seriesUid
        );
        applyCaptured({ seriesUid, originalWl });
    }, [seriesUid, image, presetName, publish, applyCaptured]);

    const toggle = useCallback(() => {
        if (captured) {
            release();
            return;
        }
        activate();
    }, [captured, release, activate]);

    /**
     * The series itself changing while active — a primary-image switch —
     * leaves nothing to restore: that series' display is not this hook's
     * to touch any more, and the captured original belongs to a series
     * that just went away. Dropped silently, never published. Read off
     * `capturedRef` rather than adding `captured` to the dependency
     * array: the effect must fire on a `seriesUid` change specifically,
     * not on every capture/release the hook's own toggle already handles
     * correctly.
     */
    useEffect(() => {
        const capturedSeriesUid = capturedRef.current?.seriesUid;
        if (
            capturedSeriesUid !== undefined &&
            capturedSeriesUid !== seriesUid
        ) {
            applyCaptured(null);
        }
    }, [seriesUid, applyCaptured]);

    /**
     * What the unmount cleanup needs, refreshed after every committed
     * render so the cleanup — which must not re-register on a series
     * change, or it would restore on every one — still reads current
     * values when it finally runs.
     */
    const onUnmountRef = useRef({ restoreOnUnmount, seriesUid, release });
    useEffect(() => {
        onUnmountRef.current = { restoreOnUnmount, seriesUid, release };
    });
    useEffect(
        () => () => {
            const latest = onUnmountRef.current;
            if (
                latest.restoreOnUnmount &&
                capturedRef.current?.seriesUid === latest.seriesUid
            ) {
                latest.release();
            }
        },
        []
    );

    /**
     * Truthfulness of the toggle. The active state claims two things —
     * the review window is on screen, and the captured original is still
     * the windowing to go back to. ANY window/level change this hook did
     * not make breaks both claims at once, so it deactivates and
     * publishes NOTHING: the review window is already gone, and
     * re-publishing a now-stale original would overwrite the windowing
     * the user just chose.
     *
     * Every path that changes a series' window/level goes through these
     * two events — the drag tool (delta), the numeric fields, the
     * viewport preset menu and its Reset (set) — so this catches all of
     * them. Subscribed only while active, so an idle caller costs
     * nothing per drag frame.
     */
    useEffect(() => {
        if (!isActive || !seriesUid) {
            return;
        }
        const deactivate = (
            event: WindowLevelSetEvent | WindowLevelDeltaEvent
        ) => {
            if (isSelfPublishingRef.current || event.seriesUid !== seriesUid) {
                return;
            }
            applyCaptured(null);
        };
        const subscriptions = [
            domainEventBus.subscribe(
                WindowLevelSetEvent.EVENT_TYPE,
                deactivate
            ),
            domainEventBus.subscribe(
                WindowLevelDeltaEvent.EVENT_TYPE,
                deactivate
            ),
        ];
        return () => subscriptions.forEach((s) => s.unsubscribe());
    }, [isActive, seriesUid, applyCaptured]);

    return useMemo(
        () => ({ isActive, toggle, activate, release }),
        [isActive, toggle, activate, release]
    );
}
